/**
 * Os passos da troca de filial — os pedacos de tela, sem estado e sem rede.
 *
 * ## Por que sao PASSOS DENTRO da folha, e nao uma segunda camada
 *
 * `DESIGN_SYSTEM §4.3`: *"Profundidade maxima de camada = 1. Um sheet nao abre outro sheet;
 * navega **dentro** do mesmo sheet, com botao 'Voltar' no cabecalho e anuncio da mudanca de
 * passo."* A confirmacao seria, pelo `§7.4`, um `Modal` de `intent="destructive"` — mas ela
 * nasce **de dentro** da folha de escolha de filial, e abrir um modal por cima de um sheet e
 * exatamente o que o `§4.3` proibe. Entao a folha muda de passo: titulo, corpo e rodape trocam,
 * e o passo e anunciado na regiao viva `status` (`§3.5`).
 *
 * O "Voltar" do `§4.3` vive no **rodape**, junto da acao, e nao no cabecalho: o `Sheet` de
 * `@farmacia/ui` nao tem espaco de cabecalho para uma acao propria (ele reserva o canto para o
 * "Fechar" de 48 px que o `§7.4` exige sempre). Poe-lo no rodape tambem alinha o passo com o
 * `§7.7`, que quer os dois verbos lado a lado com o **nao-destrutivo primeiro**.
 *
 * // DUVIDA: se o `Sheet` do design system ganhar um espaco de acao no cabecalho, o "Voltar"
 * // deste passo e o primeiro candidato a mudar de lugar. O texto nao muda.
 *
 * ## O que o `§7.7` exige de uma confirmacao destrutiva, item a item
 *
 * | Exigencia | Onde esta |
 * | --- | --- |
 * | nomear o objeto | o titulo nomeia a filial de destino |
 * | dizer o estado atual | a descricao nomeia a filial do carrinho |
 * | dizer a consequencia | a lista nomeia **cada item** em jogo, e o aviso diz o que pode sair |
 * | verbos nos dois botoes | "Continuar na Filial Centro" / "Levar para a Filial Bairro" |
 * | foco no nao-destrutivo | "Continuar na..." vem **primeiro** no DOM, e o Radix foca o primeiro focavel |
 *
 * E o que o `§8` proibe, e que aqui nao acontece: a opcao destrutiva nao vem pre-selecionada, a
 * perda nao esta escondida atras de "alguns itens", e nao ha *confirmshaming* — os dois rotulos
 * sao neutros e descrevem a acao.
 */

import type { CartItem } from '@farmacia/contracts';
import { Button } from '@farmacia/ui';
import type { ReactNode } from 'react';

import { texto } from '../conteudo/index.js';

import type { DiferencaDoCarrinho } from './troca-de-filial.js';

// ---------------------------------------------------------------------------
// Passo 2 — a confirmacao
// ---------------------------------------------------------------------------

export interface CorpoDaConfirmacaoProps {
  /** Nome de vitrine da filial de DESTINO. */
  readonly nomeAlvo: string;
  readonly itens: readonly CartItem[];
}

/**
 * A lista nomeia **todos** os itens do carrinho, e nao um resumo contado.
 *
 * Nao da para saber antes da escrita quais deles a outra filial recusa: `PUT /v1/cart/store`,
 * a rota que devolveria o diff como aviso (`API §6.9`), e fase `P`. O honesto e mostrar o que
 * esta em jogo — que e tudo — e nomear a perda no passo seguinte, quando ela for fato. O que
 * nao pode acontecer e a frase "alguns itens podem mudar" sozinha, que e a mensagem generica do
 * catalogo (`API §2.2`, `CART_STORE_MISMATCH`) e nao diz a ninguem o que perguntar.
 */
export function CorpoDaConfirmacaoDeTroca({
  nomeAlvo,
  itens,
}: CorpoDaConfirmacaoProps): ReactNode {
  return (
    <div className="troca-de-filial">
      <p className="troca-de-filial__intro">{texto('loja.troca.itens', { loja: nomeAlvo })}</p>
      <ul className="lista-simples">
        {itens.map((item) => (
          <li key={item.id}>
            {texto('loja.troca.item', {
              produto: item.displayName,
              quantidade: item.quantity,
            })}
          </li>
        ))}
      </ul>
      <p className="aviso-inline">{texto('loja.troca.aviso', { loja: nomeAlvo })}</p>
    </div>
  );
}

export interface RodapeDaConfirmacaoProps {
  readonly nomeAlvo: string;
  readonly nomeAtual: string;
  readonly aoManter: () => void;
  readonly aoConfirmar: () => void;
  readonly trocando: boolean;
}

export function RodapeDaConfirmacaoDeTroca({
  nomeAlvo,
  nomeAtual,
  aoManter,
  aoConfirmar,
  trocando,
}: RodapeDaConfirmacaoProps): ReactNode {
  return (
    <div className="acoes-fixas">
      {/*
        PRIMEIRO no DOM de proposito (`§7.4`, `§7.7`): o Radix foca o primeiro elemento focavel
        da camada, e a acao destrutiva nao pode ser o padrao focado. Inverter estas duas linhas
        e o suficiente para transformar esta tela num dark pattern.
      */}
      <Button variant="secondary" size="lg" fullWidth onClick={aoManter} disabled={trocando}>
        {texto('loja.troca.manter', { atual: nomeAtual })}
      </Button>
      <Button
        variant="destructive"
        size="lg"
        fullWidth
        onClick={aoConfirmar}
        loading={trocando}
        loadingLabel={texto('loja.troca.conferindo', { loja: nomeAlvo })}
      >
        {texto('loja.troca.confirmar', { loja: nomeAlvo })}
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Passo 3 — o que a troca custou
// ---------------------------------------------------------------------------

export interface ListaDeMudancasProps {
  readonly diferenca: DiferencaDoCarrinho;
  /** A filial NOVA — e ela que nao tem o item, e o nome dela e que faz a frase servir. */
  readonly nomeDaLoja: string;
}

/**
 * A lista do que saiu e do que encolheu — `DOMAIN` RN-076, *"trocar de filial revalida tudo e
 * **informa perdas**"*, e INV-C1, *"devolve a lista de linhas perdidas"*.
 *
 * Mora aqui, e nao dentro da folha, porque a mesma lista aparece em dois lugares: no passo 3 da
 * folha (quando a troca sai do cabecalho) e na tela de carrinho (quando ela sai do aviso de
 * divergencia). Duas grafias da mesma frase e a segunda grafia envelhecendo sozinha
 * (`DESIGN_SYSTEM §9.5`).
 */
export function ListaDeMudancas({ diferenca, nomeDaLoja }: ListaDeMudancasProps): ReactNode {
  return (
    <ul className="lista-simples">
      {diferenca.perdidos.map((linha) => (
        <li key={`saiu-${linha.productId}`}>
          {texto('loja.troca.resumo.saiu', { produto: linha.displayName, loja: nomeDaLoja })}
        </li>
      ))}
      {diferenca.reduzidos.map((linha) => (
        <li key={`reduziu-${linha.productId}`}>
          {texto('loja.troca.resumo.reduziu', {
            produto: linha.displayName,
            quantidade: linha.para,
            anterior: linha.de,
            loja: nomeDaLoja,
          })}
        </li>
      ))}
    </ul>
  );
}
